// @flow

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from 'reactstrap';
import StyleClasses from '../../styles/StyleClasses';
import { jsonGetRequest } from '../../store/requests';

type PropsType = {
    billId: string,
    sessionId: ?string,
    userId: ?string,
    payerUserId: ?string,
    coveredByName: ?string,
};

type StateType = {
    linkedBills: Array<Object>,
    coveredBills: Array<Object>,
    loading: boolean,
};

class BillLinkedPanel extends Component<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            linkedBills: [],
            coveredBills: [],
            loading: true,
        };
    }

    componentDidMount() {
        this.loadLinkedBills();
    }

    componentDidUpdate(prevProps: PropsType) {
        if (
            prevProps.billId !== this.props.billId ||
            prevProps.sessionId !== this.props.sessionId ||
            prevProps.userId !== this.props.userId
        ) {
            this.loadLinkedBills();
        }
    }

    loadLinkedBills() {
        const { sessionId, userId, billId } = this.props;
        const promises = [];

        this.setState({ loading: true, linkedBills: [], coveredBills: [] });

        // Find other bills in the same session
        if (sessionId) {
            const q = JSON.stringify({ location_session_id: { mode: 'eq', value: sessionId } });
            promises.push(
                jsonGetRequest(`bills?query=${encodeURIComponent(q)}`)
                    .then((bills) => ({ type: 'session', bills: (bills || []).filter((b) => b.id !== billId) }))
                    .catch(() => ({ type: 'session', bills: [] }))
            );
        }

        // Find bills this user paid for (covered bills)
        if (userId) {
            const q = JSON.stringify({ payer_user_id: { mode: 'eq', value: userId } });
            promises.push(
                jsonGetRequest(`bills?query=${encodeURIComponent(q)}`)
                    .then((bills) => ({ type: 'covered', bills: (bills || []).filter((b) => b.id !== billId) }))
                    .catch(() => ({ type: 'covered', bills: [] }))
            );
        }

        if (promises.length === 0) {
            this.setState({ loading: false });
            return;
        }

        Promise.all(promises).then((results) => {
            const sessionBills = (results.find((r) => r.type === 'session') || {}).bills || [];
            const coveredBills = (results.find((r) => r.type === 'covered') || {}).bills || [];
            // Exclude from sessionBills anything already in coveredBills
            const coveredIds = new Set(coveredBills.map((b) => b.id));
            this.setState({
                linkedBills: sessionBills.filter((b) => !coveredIds.has(b.id)),
                coveredBills,
                loading: false,
            });
        });
    }

    render() {
        const { payerUserId, coveredByName } = this.props;
        const { linkedBills, coveredBills, loading } = this.state;

        const hasCoverage = payerUserId || coveredByName;
        const hasLinked = linkedBills.length > 0;
        const hasCovered = coveredBills.length > 0;

        if (!hasCoverage && !hasLinked && !hasCovered && !loading) {
            return null;
        }

        return (
            <div className={StyleClasses.spacedElement}>
                {hasCoverage && (
                    <div style={{ marginBottom: '12px' }}>
                        <h4>Coverage</h4>
                        {coveredByName && (
                            <p>
                                Covered by: <strong>{coveredByName}</strong>
                            </p>
                        )}
                        {payerUserId && (
                            <p>
                                Payer:{' '}
                                <Link to={`/user-info/${payerUserId}`}>
                                    <Badge color="primary" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                                        {payerUserId}
                                    </Badge>
                                </Link>
                            </p>
                        )}
                    </div>
                )}

                {loading && <p>Loading linked bills...</p>}

                {!loading && hasCovered && (
                    <div style={{ marginBottom: '12px' }}>
                        <h4>Paid For (Covered Bills)</h4>
                        {coveredBills.map((bill) => (
                            <div key={bill.id} style={{ marginBottom: '6px' }}>
                                <Link to={`/bill-info/${bill.id}`}>
                                    <Badge color="success" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                                        {bill.id}
                                    </Badge>
                                </Link>
                                {bill.user_id && (
                                    <span style={{ marginLeft: '8px' }}>
                                        <Link to={`/user-info/${bill.user_id}`}>
                                            <Badge color="secondary" style={{ fontSize: '0.8rem', cursor: 'pointer' }}>
                                                {bill.user_id}
                                            </Badge>
                                        </Link>
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {!loading && hasLinked && (
                    <div>
                        <h4>Linked Bills (Same Session)</h4>
                        {linkedBills.map((bill) => (
                            <div key={bill.id} style={{ marginBottom: '6px' }}>
                                <Link to={`/bill-info/${bill.id}`}>
                                    <Badge color="info" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                                        {bill.id}
                                    </Badge>
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

export default BillLinkedPanel;
